import { create } from 'zustand';
import {
    persist,
    createJSONStorage,
    subscribeWithSelector
} from 'zustand/middleware';
import { share } from 'shared-zustand';
// utils
import { APP_VERSION } from '@/const';
import { flushLocalCustomerData } from '@/utils/flush.ts';

interface IAppState {
    accessToken?: string;
    refreshToken?: string;
    isAuthorized: boolean;
    expireAt?: number;
    customerId?: string;
    lastTimeUpdateAt?: number;
    isAttrsHiddenShown: boolean;
    setCustomerId: (customerId: string) => void;
    setTokens: (
        accessToken: string,
        refreshToken: string,
        expireIn: number
    ) => void;
    logout: () => void;
    removeAccessToken: () => void;
    flush: () => void;
    setAttrsHiddenShown: (sh: boolean) => void;
    toggleAttrsHiddenShown: () => void;
}

const initialState = {
    accessToken: undefined,
    refreshToken: undefined,
    expireAt: 0,
    lastTimeUpdateAt: 0,
    isAuthorized: false,
    customerId: undefined,
    isAttrsHiddenShown: false
};

const SECOND_IN_MS = 1000;

export const useAppStore = create<IAppState>()(
    persist(
        subscribeWithSelector((set) => ({
            ...initialState,
            setCustomerId: (customerId) => {
                set({ customerId });
            },
            setTokens: (accessToken, refreshToken, expireIn) => {
                const now = Date.now();
                set({
                    accessToken,
                    refreshToken,
                    isAuthorized: true,
                    expireAt: now + expireIn * SECOND_IN_MS,
                    lastTimeUpdateAt: now
                });
            },
            logout: () => {
                set(initialState);
                flushLocalCustomerData();
            },
            removeAccessToken: () => {
                set({
                    accessToken: undefined,
                    isAuthorized: false,
                    expireAt: 0,
                    lastTimeUpdateAt: 0
                });
            },
            flush: () => {
                set({
                    customerId: undefined
                });
            },
            setAttrsHiddenShown: (v: boolean) => {
                set({
                    isAttrsHiddenShown: v
                });
            },
            toggleAttrsHiddenShown: () => {
                set((state) => ({
                    isAttrsHiddenShown: !state.isAttrsHiddenShown
                }));
            }
        })),
        {
            name: 'app-store',
            storage: createJSONStorage(() => globalThis.localStorage),
            partialize: (state) => state,
            version: APP_VERSION
        }
    )
);

if ('BroadcastChannel' in globalThis) {
    share('expireAt', useAppStore);
    share('accessToken', useAppStore);
    share('refreshToken', useAppStore);
    share('isAuthorized', useAppStore);
    share('lastTimeUpdateAt', useAppStore);
}

export default useAppStore;
